/**
 * Project Time Tracking Service
 * Persists explicit and implied time entries to Firestore
 */

import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import type { ProjectTimeEntry } from '../types';
import { deleteFirestoreDocsInBatches } from './firestoreBatchDelete';

const COLLECTION = 'projectTimeEntries';

function toTimestamp(value: Date | Timestamp | undefined | null): Timestamp | null {
  if (!value) return null;
  if (value instanceof Date) return Timestamp.fromDate(value);
  return value;
}

function toFirestore(entry: Omit<ProjectTimeEntry, 'id'>): Record<string, unknown> {
  const date = entry.date instanceof Date ? Timestamp.fromDate(entry.date) : entry.date;
  const startTime = toTimestamp(entry.startTime);
  const endTime = toTimestamp(entry.endTime);
  return {
    projectId: entry.projectId,
    userId: entry.userId,
    userName: entry.userName,
    type: entry.type ?? 'explicit',
    minutes: entry.minutes,
    date,
    startTime,
    endTime,
    note: entry.note ?? null,
    taskId: entry.taskId ?? null,
    phase: entry.phase ?? null,
    createdAt: entry.createdAt instanceof Date ? Timestamp.fromDate(entry.createdAt) : entry.createdAt,
  };
}

function fromFirestore(docSnap: { id: string; data: () => Record<string, unknown> }): ProjectTimeEntry {
  const d = docSnap.data();
  const toDate = (v: unknown): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return v;
    if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
      return (v as { toDate: () => Date }).toDate();
    }
    return new Date(v as string);
  };
  return {
    id: docSnap.id,
    projectId: d.projectId as string,
    userId: d.userId as string,
    userName: d.userName as string,
    type: (d.type as ProjectTimeEntry['type']) ?? 'explicit',
    minutes: d.minutes as number,
    date: toDate(d.date) ?? new Date(),
    startTime: toDate(d.startTime),
    endTime: toDate(d.endTime),
    note: d.note as string | undefined,
    taskId: d.taskId as string | undefined,
    phase: d.phase as ProjectTimeEntry['phase'],
    createdAt: toDate(d.createdAt) ?? new Date(),
  };
}

/** Save a single time entry */
export async function saveProjectTimeEntry(entry: Omit<ProjectTimeEntry, 'id'>): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), toFirestore(entry));
  return ref.id;
}

/** Update a time entry */
export async function updateProjectTimeEntry(
  id: string,
  updates: Partial<Pick<ProjectTimeEntry, 'minutes' | 'startTime' | 'endTime' | 'date' | 'note'>>,
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  const data: { minutes?: number; note?: string | null; startTime?: Timestamp; endTime?: Timestamp; date?: Timestamp } = {};
  if (updates.minutes !== undefined) data.minutes = updates.minutes;
  if (updates.note !== undefined) data.note = updates.note ?? null;
  if (updates.startTime !== undefined) {
    data.startTime = updates.startTime instanceof Date ? Timestamp.fromDate(updates.startTime) : updates.startTime;
  }
  if (updates.endTime !== undefined) {
    data.endTime = updates.endTime instanceof Date ? Timestamp.fromDate(updates.endTime) : updates.endTime;
  }
  if (updates.date !== undefined) {
    data.date = updates.date instanceof Date ? Timestamp.fromDate(updates.date) : updates.date;
  }
  if (Object.keys(data).length > 0) {
    await updateDoc(ref, data);
  }
}

function assertEntryId(id: string): string {
  const t = typeof id === 'string' ? id.trim() : '';
  if (!t) throw new Error('Missing time entry id');
  return t;
}

/** Delete a time entry */
export async function deleteProjectTimeEntry(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, assertEntryId(id)));
}

/** Delete multiple entries — chunked for Firestore limits */
export async function deleteProjectTimeEntries(
  ids: string[],
  onProgress?: (deleted: number, total: number) => void,
): Promise<void> {
  await deleteFirestoreDocsInBatches(COLLECTION, ids.map(assertEntryId), onProgress);
}

/** Fetch all time entries for a project */
export async function getProjectTimeEntries(projectId: string): Promise<ProjectTimeEntry[]> {
  const q = query(collection(db, COLLECTION), where('projectId', '==', projectId));
  const snapshot = await getDocs(q);
  const entries = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
  return entries.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/** On-demand date-range fetch for the Settings team activity report. */
export async function getProjectTimeEntriesInRange(options: {
  start: Date;
  end: Date;
}): Promise<ProjectTimeEntry[]> {
  const q = query(
    collection(db, COLLECTION),
    where('date', '>=', Timestamp.fromDate(options.start)),
    where('date', '<=', Timestamp.fromDate(options.end)),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((d) => fromFirestore({ id: d.id, data: () => d.data() }))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
